/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { t } from "i18n";

import { Switch } from "components/switch/switch";
import Facebook from "components/icons/facebook";
import Twitter from "components/icons/twitter";
import { ContextMenu, ContextMenuList, ContextMenuItem } from "components/context-menu/context-menu";
import { SocialAccount } from "api/social-accounts";
import { PartialAppState } from "../state";
import { toggleSocialAccount, deleteSocialAccount } from "../async-action-creators";
import styles from "./social-accounts.module.css";

const getSocialShareIcon = (socialShareProvider: string) => {
  switch (socialShareProvider) {
    case "fb-page":
    case "facebook":
      return <Facebook />;
    case "twitter":
      return <Twitter />;
    default:
      return;
  }
};

interface StateProps {
  socialAccounts: SocialAccount[];
}

interface DispatchProps {
  toggleSocialAccount: (account: SocialAccount) => void;
  deleteSocialAccount: (accountId: string) => void;
}

type Props = DispatchProps & StateProps;

export const SocialAccounts: React.SFC<Props> = ({ socialAccounts, toggleSocialAccount, deleteSocialAccount }) => {
  return (
    <ul className={styles["social-accounts"]}>
      {socialAccounts.map((socialAccount) => (
        <li key={`social-account${socialAccount.id}`} className={styles["social-account"]}>
          <div className={styles["social-account-details"]}>
            <span className={styles["social-account-provider"]}>{getSocialShareIcon(socialAccount.provider)}</span>
            {t("social_share.connected_as")}
            <span className={styles["social-account-name"]}>{socialAccount["account-name"]}</span>
          </div>

          <Switch
            id={socialAccount.id.toString()}
            checked={socialAccount["is-active"]}
            onChange={() => toggleSocialAccount(socialAccount)}
          />
          <div id={`social-account-menu-${socialAccount.id}`} className={styles["social-account-more-actions"]}>
            <ContextMenu>
              <div id={`social-account-menu-${socialAccount.id}`} className={styles["social-account-menu"]}>
                <ContextMenuList>
                  <ContextMenuItem
                    label={t("social_share.delete")}
                    onClick={() => deleteSocialAccount(socialAccount["account-id"])}
                  />
                </ContextMenuList>
              </div>
            </ContextMenu>
          </div>
        </li>
      ))}
    </ul>
  );
};

const mapStateToProps = (state: PartialAppState): StateProps => {
  return {
    socialAccounts: state.socialShare.socialAccounts
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, any>): DispatchProps => {
  return {
    deleteSocialAccount: (accountId) => dispatch(deleteSocialAccount(accountId)),
    toggleSocialAccount: (account) => dispatch(toggleSocialAccount(account))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SocialAccounts);
